import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ExternalLink } from "lucide-react";
import { kwReportApi, type KwReportJob } from "@/api";

// ── Task definitions (T1~T8) ──

interface TaskDef {
  key: keyof Pick<KwReportJob,
    't1_status' | 't2_status' | 't3_status' | 't4_status' |
    't5_status' | 't6_status' | 't7_status' | 't8_status'>;
  label: string;
  labelEn: string;
  timeout: string;
}

const TASKS: TaskDef[] = [
  { key: 't1_status', label: 'T1 SERP 爬取',     labelEn: 'SERP Scraping',       timeout: '5min' },
  { key: 't2_status', label: 'T2 競品頁面爬取',   labelEn: 'Competitor Scraping', timeout: '30min' },
  { key: 't3_status', label: 'T3 Alibaba 爬取',   labelEn: 'Alibaba Search',      timeout: '10min' },
  { key: 't4_status', label: 'T4 APP16 匯入',     labelEn: 'KWI Import',          timeout: '2min' },
  { key: 't5_status', label: 'T5 Coverage 分析',  labelEn: 'Coverage Analysis',   timeout: '15min' },
  { key: 't6_status', label: 'T6 翻譯',           labelEn: 'Translation',         timeout: '20min' },
  { key: 't7_status', label: 'T7 分類',           labelEn: 'Classification',      timeout: '15min' },
  { key: 't8_status', label: 'T8 報告書生成',     labelEn: 'Report Generation',   timeout: '3min' },
];

// ── Status helpers ──

type StepStatus = 'pending' | 'running' | 'completed' | 'failed' | 'not_applicable';

function normalizeStatus(raw: string | undefined): StepStatus {
  if (!raw || raw === 'pending') return 'pending';
  if (raw === 'running') return 'running';
  if (raw === 'completed' || raw === 'completed_empty') return 'completed';
  if (raw === 'failed') return 'failed';
  if (raw === 'not_applicable' || raw === 'skipped') return 'not_applicable';
  return 'pending';
}

function StatusIcon({ status }: { status: StepStatus }) {
  if (status === 'pending') return <span className="text-gray-400">⬜</span>;
  if (status === 'running') return <span className="animate-pulse text-blue-500">🔵</span>;
  if (status === 'completed') return <span>✅</span>;
  if (status === 'failed') return <span>❌</span>;
  if (status === 'not_applicable') return <span className="text-gray-300">➖</span>;
  return <span className="text-gray-400">⬜</span>;
}

function StatusBadge({ status }: { status: StepStatus }) {
  const variants: Record<StepStatus, string> = {
    pending: 'bg-gray-100 text-gray-500',
    running: 'bg-blue-100 text-blue-700',
    completed: 'bg-green-100 text-green-700',
    failed: 'bg-red-100 text-red-700',
    not_applicable: 'bg-gray-50 text-gray-400',
  };
  const labels: Record<StepStatus, string> = {
    pending: '等待',
    running: '執行中',
    completed: '完成',
    failed: '失敗',
    not_applicable: '不適用',
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${variants[status]}`}>
      {labels[status]}
    </span>
  );
}

// ── Main component ──

interface JobProgressCardProps {
  jobId: string;
  keyword: string;
  onComplete?: (job: KwReportJob) => void;
}

export default function JobProgressCard({ jobId, keyword, onComplete }: JobProgressCardProps) {
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const { data, isError } = useQuery({
    queryKey: ['kw-report-status', jobId],
    queryFn: async () => {
      const r = await kwReportApi.status(jobId);
      return r.data as KwReportJob;
    },
    refetchInterval: (query) => {
      const job = query.state.data;
      if (!job) return 3000;
      return job.status === 'running' ? 3000 : false;
    },
    enabled: !!jobId,
  });

  useEffect(() => {
    if (data && (data.status === 'completed' || data.status === 'failed')) {
      onCompleteRef.current?.(data);
    }
  }, [data?.status]);

  const job = data;

  const completedSteps = job
    ? TASKS.filter(t => normalizeStatus(job[t.key] as string) === 'completed').length
    : 0;

  const overallStatus = isError ? 'failed' : (job?.status ?? 'running');

  return (
    <Card className="border-blue-200 bg-blue-50/30">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">
            Pipeline Progress — <span className="text-blue-700">{keyword}</span>
          </CardTitle>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">{completedSteps}/{TASKS.length} steps</span>
            <Badge
              variant="outline"
              className={
                overallStatus === 'running' ? 'border-blue-400 text-blue-700 animate-pulse' :
                overallStatus === 'completed' ? 'border-green-400 text-green-700' :
                overallStatus === 'failed' ? 'border-red-400 text-red-700' :
                'border-gray-300 text-gray-500'
              }
            >
              {overallStatus === 'running' ? '執行中' :
               overallStatus === 'completed' ? '已完成' :
               overallStatus === 'failed' ? '失敗' : '等待'}
            </Badge>
          </div>
        </div>

        {/* Progress bar */}
        {TASKS.length > 0 && (
          <div className="h-1.5 bg-gray-200 rounded-full mt-2 overflow-hidden">
            <div
              className="h-full bg-blue-500 rounded-full transition-all duration-500"
              style={{ width: `${(completedSteps / TASKS.length) * 100}%` }}
            />
          </div>
        )}
      </CardHeader>

      <CardContent className="pt-0">
        <div className="space-y-2">
          {TASKS.map((task) => {
            const status = normalizeStatus(job?.[task.key] as string);
            const isT1Done = task.key === 't1_status' && status === 'completed' && job?.t1_request_id;

            return (
              <div
                key={task.key}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                  status === 'running' ? 'bg-blue-100/60' :
                  status === 'completed' ? 'bg-green-50' :
                  status === 'failed' ? 'bg-red-50' :
                  'bg-white/60'
                }`}
              >
                <StatusIcon status={status} />
                <span className="flex-1 font-medium text-gray-700">{task.label}</span>
                <span className="text-xs text-gray-400">{task.labelEn}</span>
                <span className="text-xs text-gray-300">(max {task.timeout})</span>
                <StatusBadge status={status} />
                {isT1Done && (
                  <a
                    href={`/analysis-reports/${job!.t1_request_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-500 hover:text-blue-700"
                    title="View SERP report"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                )}
              </div>
            );
          })}
        </div>

        {isError && (
          <p className="text-xs text-red-500 mt-2">Unable to fetch job status</p>
        )}

        {job?.error_message && (
          <p className="text-xs text-red-500 mt-2 bg-red-50 rounded p-2">
            Error: {job.error_message}
          </p>
        )}

        <p className="text-xs text-gray-400 mt-3">
          Job ID: {jobId} · T4~T8 由 APP16 處理（Phase 3）
        </p>
      </CardContent>
    </Card>
  );
}
